import { message, notification, Spin } from 'ant-design-vue'
import axios from 'axios'
import type { AxiosResponse, AxiosRequestConfig } from 'axios'
import { createApp, h, nextTick } from 'vue'
import type { VNode } from 'vue'
import { isDev } from './env'

type HttpConfig = AxiosRequestConfig & {
  proxyEnv?: string
  loading?: boolean
  hideToast?: boolean
  replaceToast?: Record<string, string>
}

message.config({ maxCount: 1 })

const env = import.meta.env.VITE_API_ENV
let requestCount = 0
const prevent = (e: Event) => {
  e.stopImmediatePropagation()
  e.preventDefault()
}
const loading = () => {
  if (requestCount === 0) {
    nextTick().then(() => {
      const dom = document.createElement('div')
      dom.setAttribute('id', 'loading')
      dom.addEventListener('mousedown', prevent)
      dom.addEventListener('touchstart', prevent)
      dom.addEventListener('contextmenu', prevent)
      dom.style.position = 'fixed'
      dom.style.left = '0'
      dom.style.top = '0'
      dom.style.width = '100vw'
      dom.style.height = '100vh'
      dom.style.display = 'flex'
      dom.style.justifyContent = 'center'
      dom.style.alignItems = 'center'
      dom.style.zIndex = '999'
      dom.style.fontSize = '20px'
      document.body.appendChild(dom)
      createApp(Spin).mount(dom)
    })
  }
  requestCount += 1
}
const hideLoading = () => {
  requestCount -= 1
  const loadingDom = document.getElementById('loading')
  if (requestCount === 0 && loadingDom) {
    document.body.removeChild(loadingDom)
  }
}

const triggerDownload = (url: string, filename = '') => {
  const link = document.createElement('a')
  link.style.display = 'none'
  // a 标签的 download 属性就是下载下来的文件名
  link.download = filename as string
  link.href = url
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const commonHeader = () => ({
  'x-requested-with': 'XMLHttpRequest',
  Authorization: localStorage.getItem('Authorization') || false,
})

export const preview = (uuid: string) => {
  return `/common/api/file/preview-file/${uuid}`
}

export function getUrl(url: string) {
  return isDev ? `/${env}${url}` : url
}

function notificationError(msg: string | VNode) {
  notification.error({
    class: 'ea-notification-error',
    message: msg,
    duration: null,
  })
}

/**
 * 请求
 * @param isDownload 只有响应类型是Blob时使用，true: 下载 false: 预览 undefined(不传): 按常规接口处理
 */
const http: <T>(config: HttpConfig, isDownload?: boolean) => Promise<T> = (config, isDownload) => {
  return new Promise((resolve, reject) => {
    config.validateStatus = status => {
      return status < 500
    }
    config.headers = {
      ...commonHeader(),
      ...config.headers,
    }

    config.baseURL = isDev ? `/${env}` : '/'
    if (config.proxyEnv) {
      delete config.proxyEnv
    }
    if (!config.method) config.method = 'post'
    if (isDownload !== undefined) {
      config.responseType = 'blob'
      config.loading = false
    }
    if (config.loading !== false) {
      loading()
    }
    axios(config)
      .then(async (response: AxiosResponse) => {
        const { headers } = response
        const res = response.data
        if (response.status === 200) {
          if (isDownload !== undefined && res instanceof Blob) {
            if (headers['content-type']?.includes('json')) {
              response.data.text().then((data: string) => {
                try {
                  notificationError(JSON.parse(data).message)
                } catch {
                  reject()
                }
              })
              return
            }
            const mimeType = headers['content-type'] ?? 'application/pdf'
            const objectURL = window.URL.createObjectURL(new Blob([res], { type: mimeType }))
            if (isDownload) {
              const filename = decodeURIComponent(
                headers['content-disposition']?.split(';')[1].split('=')[1] as string,
              )
              triggerDownload(objectURL, filename)
            } else {
              window.open(objectURL)
            }
            // 释放的 URL 对象以及移除 a 标签
            URL.revokeObjectURL(objectURL)
            resolve(res as any)
            return
          }

          if (typeof res === 'object') {
            if (env === 'mock') {
              resolve(res.data)
              return
            }
            if (res.code === 200) {
              resolve(res.data)
              return
            }
            if (res.code === 10203002) {
              notificationError(res.message)
              // eslint-disable-next-line no-console
              console.log('没权限：', config.url)
              return
            }
            // 未登录或登录信息失效
            if (res.code === 10203001 || res.code === 10203003) {
              let params = ''
              if (res?.data?.isRedirectToOriginalPage) {
                params = `?redirectUrl=${encodeURIComponent(window.location.href)}`
              }
              if (window.location.pathname === '/design/') {
                window.location.href = `/#/login`
              } else {
                //  if (config.url !== '/ram/api/employee/get-employee-organization-role') {
                window.location.replace(`/#/login${params}`)
              }
              localStorage.removeItem('userInfo')
              localStorage.removeItem('Authorization')
              // window.location.reload()
              return
            }
          }
        }
        if (headers.redirect_url) {
          window.location.href = headers.redirect_url
        }
        // const errorRes = {
        //   message: '请求错误',
        //   ...(res || {}),
        // }
        if (response.data.code !== 100956) {
          if (config.url?.endsWith('list-organization-sub-folder-and-project')) {
            return
          }
          const index = config.url.indexOf('/api/')
          if (config.replaceToast?.[res.message]) {
            notificationError(config.replaceToast?.[res.message])
          } else if (!config.hideToast) {
            notificationError(
              h('div', [
                h('div', ['请求接口错误']),
                h('div', [`网络Code：${response.status}`]),
                h('div', [`业务Code：${res.code ?? ''}`]),
                h('div', [`异常信息：${res.message ?? ''}`]),
                h('div', [`接口地址：${config.url.slice(index + 4)}`]),
                h('div', [
                  `参数：`,
                  h('pre', [`${JSON.stringify(config.data ?? config.params ?? '{}', null, 2)}`]),
                ]),
              ]),
            )
          }
        }
        reject(response)
      })
      .catch(error => {
        if (!config.hideToast) {
          const res = error.response?.data ?? {}
          const index = config.url?.indexOf('/api/') ?? -1
          notificationError(
            h('div', [
              h('div', ['请求接口错误']),
              h('div', [`网络Code：${error.response?.status ?? '无响应'}`]),
              h('div', [`业务Code：${res.code ?? ''}`]),
              h('div', [`异常信息：${res.message ?? error.message ?? ''}`]),
              h('div', [`接口地址：${config.url ? config.url.slice(index + 4) : ''}`]),
              h('div', [
                `参数：`,
                h('pre', [`${JSON.stringify(config.data ?? config.params ?? '{}', null, 2)}`]),
              ]),
            ]),
          )
        }
        reject(error)
      })
      .finally(() => {
        if (config.loading !== false) {
          hideLoading()
        }
      })
  })
}

export const postExport = (url: string, data: any, isDownload = true) => {
  return http(
    {
      url,
      data,
    },
    isDownload,
  )
}

export default http
